import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { deliveryWindow } from '@theme/vv-delivery-calendar';

/** @extends {Component<{range: HTMLElement, dispatch: HTMLElement, order: HTMLElement}>} */
class VVProductDelivery extends Component {
  #listeners;
  #timer;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal: this.#listeners.signal });
    document.addEventListener('visibilitychange', this.handleVisibility, { signal: this.#listeners.signal });
    this.renderDates();
    this.#timer = window.setInterval(() => { if (!document.hidden) this.renderDates(); }, 60000);
  }

  updatedCallback() {
    super.updatedCallback();
    this.renderDates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    window.clearInterval(this.#timer);
    this.#generation++;
  }

  handleVisibility = () => { if (!document.hidden) this.renderDates(); };

  renderDates() {
    if (this.dataset.enabled !== 'true' || !this.refs.range) return;
    const locale = document.documentElement.lang || 'fr';
    const format = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
    const dates = deliveryWindow(new Date(), {
      preparationDays: Number(this.dataset.preparationDays) || 1,
      transitMin: Number(this.dataset.transitMin) || 3,
      transitMax: Number(this.dataset.transitMax) || 5,
      excludedDates: (this.dataset.excludedDates || '').split(/[\s,;]+/)
    });
    const label = this.dataset.rangeLabel || '{start} – {end}';
    this.refs.range.textContent = label.replace('{start}', format.format(dates.earliest)).replace('{end}', format.format(dates.latest));
    this.refs.range.dataset.earliest = dates.earliest.toISOString().slice(0, 10);
    this.refs.range.dataset.latest = dates.latest.toISOString().slice(0, 10);
    if (this.refs.dispatch) {
      this.refs.dispatch.textContent = format.format(dates.dispatch);
      this.refs.dispatch.setAttribute('datetime', dates.dispatch.toISOString().slice(0, 10));
    }
    if (this.refs.order) {
      this.refs.order.textContent = format.format(dates.order);
      this.refs.order.setAttribute('datetime', dates.order.toISOString().slice(0, 10));
    }
  }

  /** @param {Event & {promise?: Promise<{detail?: {html?: Document}}>}} event */
  handleProductSelect = async event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const generation = ++this.#generation;
    try {
      const result = await event.promise;
      if (!this.isConnected || generation !== this.#generation || !result?.detail?.html) return;
      const next = result.detail.html.querySelector('vv-product-delivery[data-block-id="' + CSS.escape(this.dataset.blockId || '') + '"]');
      if (!next) return;
      this.dataset.enabled = next.dataset.enabled;
      this.replaceChildren(...Array.from(next.childNodes, node => node.cloneNode(true)));
      this.updatedCallback();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[vv-product-delivery] Could not update delivery estimate.', error);
    }
  };
}
if (!customElements.get('vv-product-delivery')) customElements.define('vv-product-delivery', VVProductDelivery);
